import React from 'react';

import { SCREENING_QUESTIONS } from './constants/questions';
import { ScreeningProvider } from './ScreeningProviderRow';
import { ScreeningCategoryCard } from './ScreeningQuestion';
import {
  ScreeningAnswer,
  ScreeningAnswers,
  ScreeningCategory,
  ScreeningTextFields,
} from './types';

interface ScreeningChecklistProps {
  answers: ScreeningAnswers;
  onAnswerChange: (questionId: string, answer: ScreeningAnswer) => void;
  shouldShowSubQuestions: (parentQuestionId: string) => boolean;
  shouldShowBackupQuestion: (parentQuestionId: string) => boolean;
  answeredCount: number;
  /** Text field data for sub-questions */
  textFields?: ScreeningTextFields;
  /** Callback for text field changes */
  onTextFieldChange?: (
    questionId: string,
    fieldLabel: string,
    value: string,
  ) => void;
  /** Track which questions have been explicitly interacted with */
  touchedQuestions?: Set<string>;
  /** Whether header fields are complete (for progressive pulsing) */
  isHeaderComplete?: boolean;
  /** Dynamic questions from API (falls back to default if not provided) */
  questions?: ScreeningCategory[];
  /** Provider services added during screening */
  providers?: ScreeningProvider[];
  /** Callback when a provider is added */
  onProviderAdd?: (id: string, name: string) => void;
  /** Callback when a provider is removed */
  onProviderRemove?: (id: string) => void;
}

export const ScreeningChecklist: React.FC<ScreeningChecklistProps> = ({
  answers,
  onAnswerChange,
  shouldShowSubQuestions,
  shouldShowBackupQuestion,
  answeredCount,
  textFields = {},
  onTextFieldChange,
  touchedQuestions = new Set(),
  isHeaderComplete = false,
  questions,
  providers = [],
  onProviderAdd,
  onProviderRemove,
}) => {
  // Use provided questions or fall back to default hardcoded questions
  const screeningQuestions = questions?.length
    ? questions
    : SCREENING_QUESTIONS;

  const getAnswer = (questionId: string): ScreeningAnswer => {
    return answers[questionId] || 'unknown';
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between pb-2 mb-2 border-b border-zinc-200">
        <h2 className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">
          Qualification
        </h2>
        <span className="text-[10px] text-zinc-500">
          {answeredCount} answered
        </span>
      </div>

      {/* Questions list - compact spacing */}
      <div className="flex-1 overflow-y-auto space-y-1.5">
        {screeningQuestions.map((category) => (
          <ScreeningCategoryCard
            key={category.id}
            categoryId={category.id}
            letter={category.letter}
            mainQuestion={category.mainQuestion}
            mainAnswer={getAnswer(category.mainQuestion.id)}
            onMainAnswerChange={(answer) =>
              onAnswerChange(category.mainQuestion.id, answer)
            }
            subQuestions={category.subQuestionsOnYes}
            backupQuestion={category.backupQuestionOnNo}
            backupQuestions={category.backupQuestionsOnNo}
            getAnswer={getAnswer}
            onAnswerChange={onAnswerChange}
            showSubQuestions={shouldShowSubQuestions(category.mainQuestion.id)}
            showBackupQuestion={shouldShowBackupQuestion(
              category.mainQuestion.id,
            )}
            textFields={textFields}
            onTextFieldChange={onTextFieldChange}
            touchedQuestions={touchedQuestions}
            isHeaderComplete={isHeaderComplete}
            providers={category.id === 'treatment' ? providers : undefined}
            onProviderAdd={
              category.id === 'treatment' ? onProviderAdd : undefined
            }
            onProviderRemove={
              category.id === 'treatment' ? onProviderRemove : undefined
            }
          />
        ))}
      </div>
    </div>
  );
};
